import { useHandler } from "@chatbotgang/etude/react/useHandler";
import { css } from "@emotion/react";
import Box from "@mui/material/Box";
import Breadcrumbs from "@mui/material/Breadcrumbs";
import Stack from "@mui/material/Stack";
import Tab from "@mui/material/Tab";
import type { TabsProps } from "@mui/material/Tabs";
import Tabs from "@mui/material/Tabs";
import { theme } from "@polifonia/theme";
import { getRouteApi, Link, Navigate } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { ExternalLink } from "@/components/ExternalLink";
import { InfoBox } from "@/components/InfoBox";
import { Typography } from "@/components/Typography";
import { useEnableEmailChannelSelfOnboarding } from "@/features/email/useEnableEmailChannelSelfOnboarding";
import { interlude } from "@/interlude";
import { defineStyles } from "@/internal/emotion";
import { EmailInfoTab } from "@/pages/channels/components/EmailInfoTab";
import { ChannelQueriesContext } from "@/pages/channels/pages/detail/channelQueriesContext";
import { DnsVerifyTab } from "@/pages/channels/pages/emailOnboard/components/DnsVerifyTab";
import { DomainSetupTab } from "@/pages/channels/pages/emailOnboard/components/DomainSetupTab";
import { PageLayout } from "@/pages/components/PageLayout";
import { PageTitle } from "@/pages/components/PageTitle";

const route = getRouteApi("/channels/$type/$id");

const EMAIL_TABS = ["domain-setup", "dns-verify", "email-info"] as const;

type EmailTab = (typeof EMAIL_TABS)[number];

const styles = defineStyles({
  link: css({
    textDecoration: "none",
  }),
  pageTitle: css({
    marginBottom: 0,
  }),
  infoBox: css({
    maxWidth: "776px",
  }),
});

// ── Tab Panel ──

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel: React.FC<TabPanelProps> = function TabPanel({
  children,
  value,
  index,
}) {
  return (
    <Box
      role="tabpanel"
      hidden={value !== index}
      id={`email-tabpanel-${index}`}
      aria-labelledby={`email-tab-${index}`}
    >
      {value === index ? <Box>{children}</Box> : null}
    </Box>
  );
};

function a11yProps(index: number) {
  return {
    id: `email-tab-${index}`,
    "aria-controls": `email-tabpanel-${index}`,
  };
}

// ── Main page (tabs vary by sender domain verification status) ──

export const EmailDetailPageInternal: React.FC = () => {
  const { t } = useTranslation();
  const { id } = route.useParams();
  const queries = ChannelQueriesContext.useData();
  const channel = queries.channel;
  const enableSelfOnboarding = useEnableEmailChannelSelfOnboarding();

  // Self-onboarding tabs render from the unified channel + sender domain
  // entities. Skip the query entirely when the feature gate is off so we
  // don't fire unused requests on non-onboarding sessions.
  const senderDomainsQuery = interlude.channel.useGetChannelSenderDomains(
    { params: { channelId: channel?.id ?? 0 } },
    { enabled: enableSelfOnboarding && Boolean(channel?.id) },
  );
  const senderDomain = senderDomainsQuery.data?.[0] ?? null;

  const application = useMemo(() => {
    if (!channel || channel.type !== "email" || !senderDomain) return null;
    const verified = senderDomain.status === "verified";
    return {
      id: senderDomain.id,
      channelId: channel.id,
      senderDomain: senderDomain.domain,
      dnsRecords: senderDomain.dnsRecords,
      // Per-record validation results live in these fields, not in
      // dnsRecords[].valid — DnsVerifyTab reads them to render each record's
      // verified/pending state.
      mailCnameStatus: senderDomain.mailCnameStatus,
      dkim1Status: senderDomain.dkim1Status,
      dkim2Status: senderDomain.dkim2Status,
      dmarcStatus: senderDomain.dmarcStatus,
      status: verified ? ("active" as const) : ("pending_dns" as const),
    };
  }, [channel, senderDomain]);

  // Once DNS is verified the form is no longer editable, so we drop the
  // "寄件網域設定" tab entirely to match the onboarding page behavior. The
  // email-info tab is always available so it makes a safe fallback when
  // the active tab disappears.
  const isVerified = application?.status === "active";

  // Before verification the "Email 資訊" tab is not yet meaningful, so it is
  // hidden until the sender domain is verified — mirroring the onboarding
  // page. Without this, clicking an unverified channel from the /channels
  // list would land on the email-info tab even though DNS isn't verified.
  const visibleTabs = useMemo<ReadonlyArray<EmailTab>>(
    () =>
      isVerified
        ? (["dns-verify", "email-info"] as const)
        : (["domain-setup", "dns-verify"] as const),
    [isVerified],
  );

  const [selectedTab, setSelectedTab] = useState<EmailTab>("email-info");

  // Derive the effective tab during render instead of syncing in useEffect:
  // when the selected tab is hidden by a status change (e.g. email-info before
  // verification), fall back to a visible default without an extra render.
  // Pre-verification we land on "dns-verify" since the channel + sender domain
  // already exist and completing verification is the remaining action.
  const activeTab: EmailTab = visibleTabs.includes(selectedTab)
    ? selectedTab
    : isVerified
      ? "email-info"
      : "dns-verify";

  const tabValue = useMemo(
    () => visibleTabs.indexOf(activeTab),
    [activeTab, visibleTabs],
  );

  const tabLabels = useMemo<Record<EmailTab, string>>(
    () => ({
      "domain-setup": t("emailOnboard.tab.domainSetup", "寄件網域設定"),
      "dns-verify": t("emailOnboard.tab.dnsVerify", "DNS 驗證"),
      "email-info": t("emailOnboard.tab.emailInfo", "Email 資訊"),
    }),
    [t],
  );

  const handleTabChange = useHandler<TabsProps["onChange"]>(
    (_, newValue: number) => {
      const tab = visibleTabs[newValue];
      if (tab) setSelectedTab(tab);
    },
  );

  const handleJumpToTab = useHandler((tab: EmailTab) => {
    setSelectedTab(tab);
  });

  if (!channel || channel.type !== "email") {
    return <Navigate to="/channels" />;
  }

  if (!enableSelfOnboarding) {
    return (
      <PageLayout>
        <Stack gap="8px">
          <Breadcrumbs aria-label="breadcrumb" separator=">">
            <Link to="/channels" css={styles.link}>
              <Typography variant="body" color={theme.colors.staticFgBody}>
                {t("channels.title")}
              </Typography>
            </Link>
            <Link
              to="/channels/$type/$id"
              params={{ type: "email", id }}
              css={styles.link}
            >
              <Typography variant="body" color={theme.colors.staticFgNote}>
                {t("common.email")}
              </Typography>
            </Link>
          </Breadcrumbs>
          <PageTitle css={styles.pageTitle}>
            {t("channels.email.detail.title", "設定 Email")}
          </PageTitle>
        </Stack>
        <EmailInfoTab channel={channel} />
      </PageLayout>
    );
  }

  return (
    <PageLayout>
      <Stack gap="8px">
        <Breadcrumbs aria-label="breadcrumb" separator=">">
          <Link to="/channels" css={styles.link}>
            <Typography variant="body" color={theme.colors.staticFgBody}>
              {t("channels.title")}
            </Typography>
          </Link>
          <Link
            to="/channels/$type/$id"
            params={{ type: "email", id }}
            css={styles.link}
          >
            <Typography variant="body" color={theme.colors.staticFgNote}>
              {t("common.email")}
            </Typography>
          </Link>
        </Breadcrumbs>
        <PageTitle css={styles.pageTitle}>
          {t("channels.email.detail.title", "設定 Email")}
        </PageTitle>
      </Stack>

      <InfoBox
        sx={styles.infoBox}
        variant="plain"
        description={
          <Stack direction="column">
            {t(
              "channels.email.detail.info",
              "請依序完成寄件網域設定、DNS 驗證與 Email 資訊設定，系統會根據你填入的網域自動產出對應設定。",
            )}
            <ExternalLink
              href={t(
                "channels.email.detail.info.learnMoreUrl",
                "https://crescendolab.zendesk.com/hc/zh-tw/articles/53570915864089",
              )}
              trailingIcon={true}
            >
              {t("channels.email.detail.info.learnMore", "了解更多")}
            </ExternalLink>
          </Stack>
        }
      />

      <Tabs value={tabValue} onChange={handleTabChange}>
        {visibleTabs.map((tab, idx) => (
          <Tab key={tab} label={tabLabels[tab]} {...a11yProps(idx)} />
        ))}
      </Tabs>

      {visibleTabs.includes("domain-setup") ? (
        <TabPanel value={tabValue} index={visibleTabs.indexOf("domain-setup")}>
          <DomainSetupTab onJumpToTab={handleJumpToTab} />
        </TabPanel>
      ) : null}
      <TabPanel value={tabValue} index={visibleTabs.indexOf("dns-verify")}>
        <DnsVerifyTab onJumpToTab={handleJumpToTab} application={application} />
      </TabPanel>
      <TabPanel value={tabValue} index={visibleTabs.indexOf("email-info")}>
        <EmailInfoTab channel={channel} />
      </TabPanel>
    </PageLayout>
  );
};
